import { useState, useEffect, useMemo } from "react";
import { Download, Loader, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import Modal from "../../components/Modal";
import { clientsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useSettings } from "@/context/SettingsContext";
import { formatCurrency } from "@/lib/data";
import { formatDate } from "@/lib/dateFormat";
import { Client, LedgerData } from "../types";
import PrintableLedger from "./PrintableLedger";

interface ClientLedgerModalProps {
  client: Client | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function ClientLedgerModal({ client, isOpen, onClose }: ClientLedgerModalProps) {
  const { showToast } = useToast();
  const { settings } = useSettings();

  const [ledgerData, setLedgerData] = useState<LedgerData | null>(null);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [printLedger, setPrintLedger] = useState<LedgerData | null>(null);
  const [sortCol, setSortCol] = useState<"date" | "type" | "debit" | "credit">("date");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  const sortedEntries = useMemo(() => {
    if (!ledgerData?.entries) return [];
    return [...ledgerData.entries].sort((a, b) => {
      let cmp = 0;
      if (sortCol === "date") cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
      else if (sortCol === "type") cmp = a.type.localeCompare(b.type);
      else if (sortCol === "debit") cmp = (a.debit ?? 0) - (b.debit ?? 0);
      else if (sortCol === "credit") cmp = (a.credit ?? 0) - (b.credit ?? 0);
      return sortDir === "asc" ? cmp : -cmp;
    });
  }, [ledgerData?.entries, sortCol, sortDir]);

  const loadLedger = async (clientId: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);
      const response = await clientsApi.getLedger(clientId, params.toString());
      setLedgerData(response.data?.data || response.data);
    } catch {
      showToast("Failed to load ledger", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && client) loadLedger(client.id);
  }, [isOpen, client]);

  const handleClose = () => {
    setLedgerData(null);
    setFromDate("");
    setToDate("");
    onClose();
  };

  const downloadPdf = async () => {
    if (!client || !ledgerData) return;
    setPdfGenerating(true);
    try {
      const fromDateFormatted = fromDate ? formatDate(fromDate) : "Beginning";
      const toDateFormatted = toDate ? formatDate(toDate) : "Now";

      setPrintLedger({
        ...ledgerData,
        entries: ledgerData.entries.map(e => ({
          ...e,
          debit: e.debit,
          credit: e.credit,
        })),
        fromDate: fromDateFormatted,
        toDate: toDateFormatted,
        generatedAt: formatDate(new Date()),
      } as any);

      setTimeout(() => {
        window.print();
        setTimeout(() => setPrintLedger(null), 100);
      }, 100);

      showToast("Ledger ready — use browser's Save as PDF", "success");
    } catch {
      showToast("Failed to generate ledger", "error");
    } finally {
      setPdfGenerating(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={client ? `${client.name} — Ledger` : "Client Ledger"}
        size="lg"
      >
        <div className="space-y-4">
          {/* Date filter row */}
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="block text-xs text-stone-500 mb-1">From Date</label>
              <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400" />
            </div>
            <div className="flex-1">
              <label className="block text-xs text-stone-500 mb-1">To Date</label>
              <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400" />
            </div>
            <button
              onClick={() => client && loadLedger(client.id)}
              className="flex items-center gap-1.5 px-3 py-2 bg-stone-100 hover:bg-stone-200 rounded-xl text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Apply
            </button>
          </div>

          {/* Summary cards */}
          {ledgerData?.summary && (
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-red-50 rounded-xl p-3 border border-red-100">
                <div className="text-xs text-red-600 mb-0.5">Total Invoiced</div>
                <div className="font-bold text-red-700">{formatCurrency(ledgerData.summary.total_invoiced)}</div>
              </div>
              <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100">
                <div className="text-xs text-emerald-600 mb-0.5">Total Paid</div>
                <div className="font-bold text-emerald-700">{formatCurrency(ledgerData.summary.total_paid)}</div>
              </div>
              <div className={`rounded-xl p-3 border ${ledgerData.summary.current_balance > 0 ? "bg-amber-50 border-amber-100" : "bg-emerald-50 border-emerald-100"}`}>
                <div className={`text-xs mb-0.5 ${ledgerData.summary.current_balance > 0 ? "text-amber-600" : "text-emerald-600"}`}>Balance Due</div>
                <div className={`font-bold ${ledgerData.summary.current_balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {formatCurrency(ledgerData.summary.current_balance)}
                </div>
              </div>
            </div>
          )}

          {/* Ledger table */}
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-7 w-7 border-2 border-amber-500 border-t-transparent" />
            </div>
          ) : sortedEntries.length ? (
            <div className="overflow-x-auto border border-stone-200 rounded-xl">
              <table className="w-full text-xs">
                <thead className="bg-stone-50 border-b border-stone-200">
                  <tr>
                    {([
                      { key: "date", label: "Date", align: "left" },
                      { key: "type", label: "Type", align: "left" },
                      { key: null,   label: "Description", align: "left" },
                      { key: "debit",  label: "Debit",  align: "right" },
                      { key: "credit", label: "Credit", align: "right" },
                    ] as const).map(col => (
                      <th
                        key={col.label}
                        onClick={() => col.key && toggleSort(col.key)}
                        className={`px-3 py-2 font-semibold text-stone-500 select-none ${col.align === "right" ? "text-right" : "text-left"} ${col.key ? "cursor-pointer hover:text-stone-700" : ""}`}
                      >
                        <span className="inline-flex items-center gap-0.5">
                          {col.label}
                          {col.key && (
                            sortCol === col.key
                              ? sortDir === "asc" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />
                              : <ChevronsUpDown className="w-3 h-3 opacity-30" />
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {sortedEntries.map((entry, idx) => (
                    <tr key={idx} className="hover:bg-stone-50/70">
                      <td className="px-3 py-1.5 text-stone-500 whitespace-nowrap">
                        {entry.date ? new Date(entry.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${entry.type === "invoice" ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"}`}>
                          {entry.type}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-stone-600 max-w-[180px] truncate" title={entry.description}>{entry.description}</td>
                      <td className="px-3 py-1.5 text-right text-red-600 font-medium tabular-nums">
                        {entry.debit > 0 ? formatCurrency(entry.debit) : <span className="text-stone-300">—</span>}
                      </td>
                      <td className="px-3 py-1.5 text-right text-emerald-600 font-medium tabular-nums">
                        {entry.credit > 0 ? formatCurrency(entry.credit) : <span className="text-stone-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-stone-400 text-xs">No ledger entries found</div>
          )}

          {/* Footer */}
          <div className="flex justify-end pt-1">
            <button
              onClick={downloadPdf}
              disabled={pdfGenerating || !ledgerData}
              className="flex items-center gap-2 px-4 py-2 bg-stone-800 hover:bg-stone-900 text-white rounded-xl text-sm font-medium disabled:opacity-40 transition-colors"
            >
              {pdfGenerating ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {pdfGenerating ? "Generating…" : "Download PDF"}
            </button>
          </div>
        </div>
      </Modal>

      {printLedger && (
        <PrintableLedger
          client={printLedger.client}
          entries={(printLedger as any).entries}
          summary={printLedger.summary}
          fromDate={(printLedger as any).fromDate}
          toDate={(printLedger as any).toDate}
          generatedAt={(printLedger as any).generatedAt}
          businessInfo={{
            name: settings?.business?.businessName || "Shalimar",
            logoUrl: settings?.logoUrl,
            address: settings?.business?.address,
            phone: settings?.business?.phone,
            email: settings?.business?.email,
          }}
        />
      )}
    </>
  );
}
